import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useCookieConsent from "../hooks/useCookieConsent";
import { DEFAULT_PREFERENCES, type CookiePreferences } from "../utils/cookieConsentStore";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleRow({ label, description, checked, locked, onChange }: ToggleRowProps) {
  return (
    <label className={`flex items-start gap-3 py-3 ${locked ? "" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-teal-500"
      />
      <span>
        <span className="block text-sm font-semibold text-ocean-950 dark:text-foam-50">
          {label}
          {locked && <span className="ml-2 text-xs font-normal text-ocean-800/50 dark:text-foam-100/50">(always on)</span>}
        </span>
        <span className="block text-xs leading-relaxed text-ocean-800/60 dark:text-foam-100/60">{description}</span>
      </span>
    </label>
  );
}

export default function CookiePreferencesModal() {
  const { preferencesOpen, closePreferences, savePreferences, preferences } = useCookieConsent();
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [draft, setDraft] = useState<CookiePreferences>(preferences ?? DEFAULT_PREFERENCES);

  useEffect(() => {
    if (preferencesOpen) setDraft(preferences ?? DEFAULT_PREFERENCES);
  }, [preferencesOpen, preferences]);

  useEffect(() => {
    if (preferencesOpen) {
      const id = setTimeout(() => closeButtonRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
  }, [preferencesOpen]);

  useEffect(() => {
    if (!preferencesOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closePreferences();
      } else if (e.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [preferencesOpen, closePreferences]);

  return (
    <AnimatePresence>
      {preferencesOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.15 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-ocean-950/70 px-4 backdrop-blur-sm"
          onClick={closePreferences}
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Cookie preferences"
            initial={reducedMotion ? false : { opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl dark:bg-ocean-900"
          >
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-lg font-semibold text-ocean-950 dark:text-foam-50">Cookie Preferences</h3>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={closePreferences}
                aria-label="Close"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ocean-800/50 hover:bg-ocean-900/5 dark:text-foam-100/50 dark:hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-sm text-ocean-800/70 dark:text-foam-100/70">
              Choose which categories of cookies we can use. Essential cookies keep the site working and
              can&apos;t be turned off.
            </p>

            <div className="mt-2 divide-y divide-ocean-900/5 dark:divide-white/10">
              <ToggleRow
                label="Essential"
                description="Required for the site to function — e.g. remembering this preference."
                checked={draft.essential}
                locked
                onChange={() => {}}
              />
              <ToggleRow
                label="Analytics"
                description="Helps us understand how visitors use the site. Nothing is sent to a third party today."
                checked={draft.analytics}
                onChange={(checked) => setDraft((d) => ({ ...d, analytics: checked }))}
              />
              <ToggleRow
                label="Marketing"
                description="Would personalize offers and ads. Not currently in use."
                checked={draft.marketing}
                onChange={(checked) => setDraft((d) => ({ ...d, marketing: checked }))}
              />
            </div>

            <button
              type="button"
              onClick={() => savePreferences(draft)}
              className="mt-5 w-full rounded-full bg-teal-400 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-[1.02]"
            >
              Save Preferences
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
